// Güneş Saatleri alternatif tasarımları için normalize edilmiş view-model.
// SunTimes.js'teki `result` + `isDst` + `dstPeriod`'tan, her tasarımın doğrudan
// render edebileceği (asla undefined olmayan) string alanlar üretir.

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const WEEKDAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba',
  'Perşembe', 'Cuma', 'Cumartesi'];

const TIME_RE = /^\d{1,2}:\d{2}$/;

const pad2 = (n) => String(n).padStart(2, '0');

// "DD/MM/YYYY" -> { d, m, y } (sayısal). Geçersizse null.
const parseDmy = (s) => {
  if (!s || typeof s !== 'string') return null;
  const [d, m, y] = s.split('/').map(Number);
  if (!d || !m || !y) return null;
  return { d, m, y };
};

// ISO "YYYY-MM-DD" -> "DD.MM.YYYY"; "süresiz" -> "süresiz".
const isoToDot = (iso) => {
  if (!iso) return '';
  if (iso === 'süresiz') return 'süresiz';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
};

// "HH:MM" -> dakika; geçersizse null.
const timeToMin = (t) => {
  if (!t || !TIME_RE.test(t)) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

// Yaz saatinin uygulandığı tarih aralığı (uygulanmadıysa null).
// Kalıcı dönem → "27.03.2016 tarihinden beri (kalıcı)"; aralıklı → "GG.AA.YYYY – GG.AA.YYYY".
const buildDstRange = (isDst, dstPeriod) => {
  if (!isDst || !dstPeriod || !dstPeriod.start) return null;
  if (dstPeriod.start === '2016-03-27' && dstPeriod.end === 'süresiz') {
    return '27.03.2016 tarihinden beri (kalıcı)';
  }
  return `${isoToDot(dstPeriod.start)} – ${isoToDot(dstPeriod.end)}`;
};

const buildDstNote = (isDst, dstPeriod) => {
  if (!isDst) {
    return 'Bu tarihte yaz saati uygulanmamıştır; gösterilen saatler standart saattir.';
  }
  if (dstPeriod && dstPeriod.start === '2016-03-27' && dstPeriod.end === 'süresiz') {
    return '27.03.2016 tarihinden bu yana kalıcı yaz saati uygulanmaktadır.';
  }
  if (dstPeriod && dstPeriod.start) {
    return `${isoToDot(dstPeriod.start)} – ${isoToDot(dstPeriod.end)} tarihleri arasında yaz saati uygulanmıştır.`;
  }
  return 'Bu tarihte yaz saati uygulanmıştır.';
};

export function buildSunViewModel(result, isDst, dstPeriod) {
  const r = result || {};
  const city = r.city || '';
  const district = r.district || '';
  const location = district && city ? `${district}, ${city}` : (city || district);
  const locationSlash = district && city ? `${district} / ${city}` : (city || district);
  const locationDot = district && city ? `${district} · ${city}` : (city || district);

  // Tarih alanları (timezone-güvenli: sayısal Y/M/D ile)
  const dmy = parseDmy(r.date);
  const dateSlash = r.date || ''; // Klasik kartların beklediği GG/AA/YYYY ham hali
  let dateShort = r.date || '';
  let dateLong = '';
  let weekday = '';
  if (dmy) {
    dateShort = `${pad2(dmy.d)}.${pad2(dmy.m)}.${dmy.y}`;
    dateLong = `${pad2(dmy.d)} ${MONTHS[dmy.m - 1] || ''} ${dmy.y}`.trim();
    const dt = new Date(dmy.y, dmy.m - 1, dmy.d);
    weekday = WEEKDAYS[dt.getDay()] || '';
  }

  // Saatler: result.sunrise/sunset zaten DST uygulanmış değerler (DST yoksa = normal)
  const sunriseDst = r.sunrise || '';
  const sunsetDst = r.sunset || '';
  const sunriseNormal = r.normalSunrise || '';
  const sunsetNormal = r.normalSunset || '';

  const hasData = TIME_RE.test(sunriseDst) && TIME_RE.test(sunsetDst);

  // Gündüz süresi = batış - doğuş (gösterilen değerlerden). Gün dönüşünü topla.
  let daylightDuration = { hhmm: '—', long: '—' };
  const riseMin = timeToMin(sunriseDst);
  const setMin = timeToMin(sunsetDst);
  if (riseMin != null && setMin != null) {
    let diff = setMin - riseMin;
    if (diff < 0) diff += 24 * 60;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    daylightDuration = { hhmm: `${pad2(h)}:${pad2(m)}`, long: `${h}s ${pad2(m)}dk` };
  }

  return {
    city,
    district,
    location,
    locationSlash,
    locationDot,
    dateSlash,
    dateShort,
    dateLong,
    weekday,
    sunriseDst,
    sunriseNormal,
    sunsetDst,
    sunsetNormal,
    isDst: !!isDst,
    daylightDuration,
    dstNote: buildDstNote(isDst, dstPeriod),
    dstRange: buildDstRange(isDst, dstPeriod),
    hasData,
  };
}

export default buildSunViewModel;
